import axios from "axios";

// const baseURL = 'https://localhost:44342/api/'; //local
 const baseURL = 'https://qa.pharmacy.api.sehhasolutions.com/api/'; //prod

const apiServices = axios.create({
    baseURL,
})

// Add request interceptor to include token
apiServices.interceptors.request.use(
    (config) => {
        const token = sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
apiServices.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Handle unauthorized access
            sessionStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// ==================== DASHBOARD API ====================
export const DashboardAPI = {
    // Get dashboard statistics
    getDashboardStats: async (organizationId) => {
        try {
            const response = await apiServices.get(`Dashboard/stats/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
            throw error;
        }
    },

    // Get expiring products
    getExpiringMedications: async (organizationId, daysAhead = 30) => {
        try {
            const response = await apiServices.get(`Dashboard/expiring-medications/organization/${organizationId}?daysAhead=${daysAhead}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching expiring products:", error);
            throw error;
        }
    },

    // Get low stock products
    getLowStockMedications: async (organizationId) => {
        try {
            const response = await apiServices.get(`Dashboard/low-stock-medications/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching low stock products:", error);
            throw error;
        }
    },

    // Get recent purchase orders
    getRecentPurchaseOrders: async (organizationId, count = 5) => {
        try {
            const response = await apiServices.get(`Dashboard/recent-purchase-orders/organization/${organizationId}?count=${count}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching recent purchase orders:", error);
            throw error;
        }
    },

    // Get recent sales orders
    getRecentSalesOrders: async (organizationId, count = 5) => {
        try {
            const response = await apiServices.get(`Dashboard/recent-sales-orders/organization/${organizationId}?count=${count}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching recent sales orders:", error);
            throw error;
        }
    },

    // Get top selling products
    getTopSellingMedications: async (organizationId, topCount = 10, fromDate = null, toDate = null) => {
        try {
            let url = `Dashboard/top-selling-medications/organization/${organizationId}?topCount=${topCount}`;
            if (fromDate) url += `&fromDate=${fromDate}`;
            if (toDate) url += `&toDate=${toDate}`;
            
            const response = await apiServices.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching top selling products:", error);
            throw error;
        }
    },

    // Get inventory summary
    getInventorySummary: async (organizationId) => {
        try {
            const response = await apiServices.get(`Dashboard/inventory-summary/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching inventory summary:", error);
            throw error;
        }
    },

    // Get stock movements
    getStockMovements: async (organizationId, fromDate = null, toDate = null) => {
        try {
            let url = `Dashboard/stock-movements/organization/${organizationId}`;
            if (fromDate) url += `?fromDate=${fromDate}`;
            if (toDate) url += `${fromDate ? '&' : '?'}toDate=${toDate}`;
            
            const response = await apiServices.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching stock movements:", error);
            throw error;
        }
    }
};

// ==================== PRODUCTS API ====================
// Alias for backward compatibility - MedicationsAPI is now ProductsAPI
export const MedicationsAPI = {
    // Get all products
    getAllMedications: async (organizationId) => {
        try {
            const response = await apiServices.get(`Medications/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching products:", error);
            throw error;
        }
    },

    // Get product by ID
    getMedicationById: async (medicationId) => {
        try {
            const response = await apiServices.get(`Medications/${medicationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product:", error);
            throw error;
        }
    },

    // Create product
    createMedication: async (medicationData) => {
        try {
            const response = await apiServices.post('Medications', medicationData);
            return response.data;
        } catch (error) {
            console.error("Error creating product:", error);
            throw error;
        }
    },

    // Update product
    updateMedication: async (medicationId, medicationData) => {
        try {
            const response = await apiServices.put(`Medications/${medicationId}`, medicationData);
            return response.data;
        } catch (error) {
            console.error("Error updating product:", error);
            throw error;
        }
    },

    // Delete product
    deleteMedication: async (medicationId) => {
        try {
            const response = await apiServices.delete(`Medications/${medicationId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting product:", error);
            throw error;
        }
    },

    // Get product categories
    getMedicationCategories: async () => {
        try {
            const response = await apiServices.get('Medications/Categories');
            return response.data;
        } catch (error) {
            console.error("Error fetching product categories:", error);
            throw error;
        }
    },

    // Get expiring products
    getExpiringMedications: async (organizationId, daysAhead = 30) => {
        try {
            const response = await apiServices.get(`Medications/Expiring/${organizationId}/${daysAhead}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching expiring products:", error);
            throw error;
        }
    },

    // Get low stock products
    getLowStockMedications: async (organizationId) => {
        try {
            const response = await apiServices.get(`Medications/LowStock/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching low stock products:", error);
            throw error;
        }
    },

    // Update stock
    updateStock: async (medicationId, stockData) => {
        try {
            const response = await apiServices.post(`Medications/${medicationId}/stock`, stockData);
            return response.data;
        } catch (error) {
            console.error("Error updating stock:", error);
            throw error;
        }
    },

    // Search products
    searchMedications: async (searchTerm, organizationId) => {
        try {
            const response = await apiServices.get(`Medications/search?searchTerm=${searchTerm}&organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error searching products:", error);
            throw error;
        }
    },

    // Get product by product code
    getMedicationByProductCode: async (productCode, organizationId) => {
        try {
            const response = await apiServices.get(`Medications/product-code/${productCode}/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product by product code:", error);
            throw error;
        }
    },

    // Get products by category
    getMedicationsByCategory: async (categoryId, organizationId) => {
        try {
            const response = await apiServices.get(`Medications/category/${categoryId}/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching products by category:", error);
            throw error;
        }
    },

    // Get stock movements for a product
    getStockMovements: async (medicationId, fromDate = null, toDate = null) => {
        try {
            let url = `Medications/${medicationId}/stock-movements`;
            if (fromDate) url += `?fromDate=${fromDate}`;
            if (toDate) url += `${fromDate ? '&' : '?'}toDate=${toDate}`;
            
            const response = await apiServices.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching stock movements:", error);
            throw error;
        }
    }
};

// Export as ProductsAPI for new code
export const ProductsAPI = MedicationsAPI;

// ==================== PURCHASE ORDERS API ====================
export const PurchaseOrdersAPI = {
    // Get all purchase orders
    getAllPurchaseOrders: async (organizationId) => {
        try {
            const response = await apiServices.get(`PurchaseOrders/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching purchase orders:", error);
            throw error;
        }
    },

    // Get purchase order by ID
    getPurchaseOrderById: async (purchaseOrderId) => {
        try {
            const response = await apiServices.get(`PurchaseOrders/${purchaseOrderId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching purchase order:", error);
            throw error;
        }
    },

    // Create purchase order
    createPurchaseOrder: async (purchaseOrderData) => {
        try {
            const response = await apiServices.post('PurchaseOrders', purchaseOrderData);
            return response.data;
        } catch (error) {
            console.error("Error creating purchase order:", error);
            throw error;
        }
    },

    // Update purchase order
    updatePurchaseOrder: async (purchaseOrderId, purchaseOrderData) => {
        try {
            const response = await apiServices.put(`PurchaseOrders/${purchaseOrderId}`, purchaseOrderData);
            return response.data;
        } catch (error) {
            console.error("Error updating purchase order:", error);
            throw error;
        }
    },

    // Delete purchase order
    deletePurchaseOrder: async (purchaseOrderId) => {
        try {
            const response = await apiServices.delete(`PurchaseOrders/${purchaseOrderId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting purchase order:", error);
            throw error;
        }
    },

    // Get purchase order statuses
    getPurchaseOrderStatuses: async () => {
        try {
            const response = await apiServices.get('PurchaseOrders/Statuses');
            return response.data;
        } catch (error) {
            console.error("Error fetching purchase order statuses:", error);
            throw error;
        }
    },

    // Get purchase order details
    getPurchaseOrderDetails: async (purchaseOrderId) => {
        try {
            const response = await apiServices.get(`PurchaseOrders/${purchaseOrderId}/Details`);
            return response.data;
        } catch (error) {
            console.error("Error fetching purchase order details:", error);
            throw error;
        }
    },

    // Add purchase order detail
    addPurchaseOrderDetail: async (detailData) => {
        try {
            const response = await apiServices.post('PurchaseOrders/Details', detailData);
            return response.data;
        } catch (error) {
            console.error("Error adding purchase order detail:", error);
            throw error;
        }
    },

    // Update purchase order detail
    updatePurchaseOrderDetail: async (detailId, detailData) => {
        try {
            const response = await apiServices.put(`PurchaseOrders/Details/${detailId}`, detailData);
            return response.data;
        } catch (error) {
            console.error("Error updating purchase order detail:", error);
            throw error;
        }
    },

    // Delete purchase order detail
    deletePurchaseOrderDetail: async (detailId) => {
        try {
            const response = await apiServices.delete(`PurchaseOrders/Details/${detailId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting purchase order detail:", error);
            throw error;
        }
    },

    // Receive purchase order
    receivePurchaseOrder: async (purchaseOrderId, receiveData) => {
        try {
            const response = await apiServices.post(`PurchaseOrders/${purchaseOrderId}/receive`, receiveData);
            return response.data;
        } catch (error) {
            console.error("Error receiving purchase order:", error);
            throw error;
        }
    },

    // Search purchase orders
    searchPurchaseOrders: async (searchTerm, organizationId) => {
        try {
            const response = await apiServices.get(`PurchaseOrders/search?searchTerm=${searchTerm}&organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error searching purchase orders:", error);
            throw error;
        }
    },

    // Get purchase orders by status
    getPurchaseOrdersByStatus: async (statusId, organizationId) => {
        try {
            const response = await apiServices.get(`PurchaseOrders/status/${statusId}/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching purchase orders by status:", error);
            throw error;
        }
    },

    // Get purchase orders by supplier
    getPurchaseOrdersBySupplier: async (supplierId, organizationId) => {
        try {
            const response = await apiServices.get(`PurchaseOrders/supplier/${supplierId}/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching purchase orders by supplier:", error);
            throw error;
        }
    },

    // Update purchase order status
    updatePurchaseOrderStatus: async (purchaseOrderId, statusId, cancellationReason = null) => {
        try {
            const response = await apiServices.put(`PurchaseOrders/${purchaseOrderId}/status`, {
                statusId,
                cancellationReason
            });
            return response.data;
        } catch (error) {
            console.error("Error updating purchase order status:", error);
            throw error;
        }
    },

    // Get purchase order with details
    getPurchaseOrderWithDetails: async (purchaseOrderId) => {
        try {
            const response = await apiServices.get(`PurchaseOrders/${purchaseOrderId}/details`);
            return response.data;
        } catch (error) {
            console.error("Error fetching purchase order with details:", error);
            throw error;
        }
    }
};

// ==================== SUPPLIERS API ====================
export const SuppliersAPI = {
    // Get all suppliers
    getAllSuppliers: async (organizationId) => {
        try {
            const response = await apiServices.get(`Suppliers/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching suppliers:", error);
            throw error;
        }
    },

    // Get supplier by ID
    getSupplierById: async (supplierId) => {
        try {
            const response = await apiServices.get(`Suppliers/${supplierId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching supplier:", error);
            throw error;
        }
    },

    // Create supplier
    createSupplier: async (supplierData) => {
        try {
            const response = await apiServices.post('Suppliers', supplierData);
            return response.data;
        } catch (error) {
            console.error("Error creating supplier:", error);
            throw error;
        }
    },

    // Update supplier
    updateSupplier: async (supplierId, supplierData) => {
        try {
            const response = await apiServices.put(`Suppliers/${supplierId}`, supplierData);
            return response.data;
        } catch (error) {
            console.error("Error updating supplier:", error);
            throw error;
        }
    },

    // Delete supplier
    deleteSupplier: async (supplierId) => {
        try {
            const response = await apiServices.delete(`Suppliers/${supplierId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting supplier:", error);
            throw error;
        }
    },

    // Search suppliers
    searchSuppliers: async (searchTerm, organizationId) => {
        try {
            const response = await apiServices.get(`Suppliers/search?searchTerm=${searchTerm}&organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error searching suppliers:", error);
            throw error;
        }
    },

    // Check if supplier is active
    isSupplierActive: async (supplierId) => {
        try {
            const response = await apiServices.get(`Suppliers/${supplierId}/active`);
            return response.data;
        } catch (error) {
            console.error("Error checking supplier status:", error);
            throw error;
        }
    }
};

// ==================== CUSTOMERS API ====================
export const CustomersAPI = {
    // Get all customers
    getAllCustomers: async (organizationId) => {
        try {
            const response = await apiServices.get(`Customers/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching customers:", error);
            throw error;
        }
    },

    // Get customer by ID
    getCustomerById: async (customerId) => {
        try {
            const response = await apiServices.get(`Customers/${customerId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching customer:", error);
            throw error;
        }
    },

    // Create customer
    createCustomer: async (customerData) => {
        try {
            const response = await apiServices.post('Customers', customerData);
            return response.data;
        } catch (error) {
            console.error("Error creating customer:", error);
            throw error;
        }
    },

    // Update customer
    updateCustomer: async (customerId, customerData) => {
        try {
            const response = await apiServices.put(`Customers/${customerId}`, customerData);
            return response.data;
        } catch (error) {
            console.error("Error updating customer:", error);
            throw error;
        }
    },

    // Delete customer
    deleteCustomer: async (customerId) => {
        try {
            const response = await apiServices.delete(`Customers/${customerId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting customer:", error);
            throw error;
        }
    },

    // Search customers
    searchCustomers: async (searchTerm, organizationId) => {
        try {
            const response = await apiServices.get(`Customers/search?searchTerm=${searchTerm}&organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error searching customers:", error);
            throw error;
        }
    },

    // Get customers by phone
    getCustomersByPhone: async (phone, organizationId) => {
        try {
            const response = await apiServices.get(`Customers/phone/${phone}/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching customers by phone:", error);
            throw error;
        }
    },

    // Get customers by email
    getCustomersByEmail: async (email, organizationId) => {
        try {
            const response = await apiServices.get(`Customers/email/${email}/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching customers by email:", error);
            throw error;
        }
    }
};

// ==================== POS API ====================
export const POSAPI = {
    // Create purchase order via POS
    createPurchaseOrder: async (orderData) => {
        try {
            const response = await apiServices.post('POS', orderData);
            return response.data;
        } catch (error) {
            console.error("Error creating POS order:", error);
            throw error;
        }
    }
};

// ==================== PRODUCT SETTINGS API ====================
export const ProductSettingsAPI = {
    // Categories
    getAllCategories: async () => {
        try {
            const response = await apiServices.get('ProductSettings/categories');
            return response.data;
        } catch (error) {
            console.error("Error fetching categories:", error);
            throw error;
        }
    },

    getCategoryById: async (categoryId) => {
        try {
            const response = await apiServices.get(`ProductSettings/categories/${categoryId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching category:", error);
            throw error;
        }
    },

    createCategory: async (categoryData) => {
        try {
            const response = await apiServices.post('ProductSettings/categories', categoryData);
            return response.data;
        } catch (error) {
            console.error("Error creating category:", error);
            throw error;
        }
    },

    updateCategory: async (categoryId, categoryData) => {
        try {
            const response = await apiServices.put(`ProductSettings/categories/${categoryId}`, categoryData);
            return response.data;
        } catch (error) {
            console.error("Error updating category:", error);
            throw error;
        }
    },

    deleteCategory: async (categoryId) => {
        try {
            const response = await apiServices.delete(`ProductSettings/categories/${categoryId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting category:", error);
            throw error;
        }
    },

    // Units
    getAllUnits: async () => {
        try {
            const response = await apiServices.get('ProductSettings/units');
            return response.data;
        } catch (error) {
            console.error("Error fetching units:", error);
            throw error;
        }
    },

    getUnitById: async (unitId) => {
        try {
            const response = await apiServices.get(`ProductSettings/units/${unitId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching unit:", error);
            throw error;
        }
    },

    createUnit: async (unitData) => {
        try {
            const response = await apiServices.post('ProductSettings/units', unitData);
            return response.data;
        } catch (error) {
            console.error("Error creating unit:", error);
            throw error;
        }
    },

    updateUnit: async (unitId, unitData) => {
        try {
            const response = await apiServices.put(`ProductSettings/units/${unitId}`, unitData);
            return response.data;
        } catch (error) {
            console.error("Error updating unit:", error);
            throw error;
        }
    },

    deleteUnit: async (unitId) => {
        try {
            const response = await apiServices.delete(`ProductSettings/units/${unitId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting unit:", error);
            throw error;
        }
    },

    // Product Lookups
    getAllProductLookups: async () => {
        try {
            const response = await apiServices.get('ProductSettings/product-lookups');
            return response.data;
        } catch (error) {
            console.error("Error fetching product lookups:", error);
            throw error;
        }
    },

    getProductLookupById: async (productLookupId) => {
        try {
            const response = await apiServices.get(`ProductSettings/product-lookups/${productLookupId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product lookup:", error);
            throw error;
        }
    },

    getProductLookupsByCategory: async (category) => {
        try {
            const response = await apiServices.get(`ProductSettings/product-lookups/category/${category}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product lookups by category:", error);
            throw error;
        }
    },

    createProductLookup: async (productLookupData) => {
        try {
            const response = await apiServices.post('ProductSettings/product-lookups', productLookupData);
            return response.data;
        } catch (error) {
            console.error("Error creating product lookup:", error);
            throw error;
        }
    },

    updateProductLookup: async (productLookupId, productLookupData) => {
        try {
            const response = await apiServices.put(`ProductSettings/product-lookups/${productLookupId}`, productLookupData);
            return response.data;
        } catch (error) {
            console.error("Error updating product lookup:", error);
            throw error;
        }
    },

    deleteProductLookup: async (productLookupId) => {
        try {
            const response = await apiServices.delete(`ProductSettings/product-lookups/${productLookupId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting product lookup:", error);
            throw error;
        }
    }
};

// ==================== USER MANAGEMENT API ====================
export const UserManagementAPI = {
    getAllUsers: async (organizationId) => {
        try {
            const response = await apiServices.get(`UserManagement/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching users:", error);
            throw error;
        }
    },

    getUserById: async (userId) => {
        try {
            const response = await apiServices.get(`UserManagement/${userId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching user:", error);
            throw error;
        }
    },

    getUsersByRole: async (role, organizationId) => {
        try {
            const response = await apiServices.get(`UserManagement/role/${role}/organization/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching users by role:", error);
            throw error;
        }
    },

    createUser: async (userData) => {
        try {
            const response = await apiServices.post('UserManagement', userData);
            return response.data;
        } catch (error) {
            console.error("Error creating user:", error);
            throw error;
        }
    },

    updateUser: async (userId, userData) => {
        try {
            const response = await apiServices.put(`UserManagement/${userId}`, userData);
            return response.data;
        } catch (error) {
            console.error("Error updating user:", error);
            throw error;
        }
    },

    deleteUser: async (userId) => {
        try {
            const response = await apiServices.delete(`UserManagement/${userId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting user:", error);
            throw error;
        }
    },

    changePassword: async (userId, newPassword) => {
        try {
            const response = await apiServices.put(`UserManagement/${userId}/change-password`, { newPassword });
            return response.data;
        } catch (error) {
            console.error("Error changing password:", error);
            throw error;
        }
    },

    getRoles: async () => {
        try {
            const response = await apiServices.get('UserManagement/roles');
            return response.data;
        } catch (error) {
            console.error("Error fetching roles:", error);
            throw error;
        }
    }
};

// ==================== ORGANIZATION API ====================
export const OrganizationsAPI = {
    // Get all organizations
    getAllOrganizations: async () => {
        try {
            const response = await apiServices.get('Organizations');
            return response.data;
        } catch (error) {
            console.error("Error fetching organizations:", error);
            throw error;
        }
    },

    // Get organization by ID
    getOrganizationById: async (organizationId) => {
        try {
            const response = await apiServices.get(`Organizations/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching organization:", error);
            throw error;
        }
    },

    // Create organization
    createOrganization: async (organizationData) => {
        try {
            const response = await apiServices.post('Organizations', organizationData);
            return response.data;
        } catch (error) {
            console.error("Error creating organization:", error);
            throw error;
        }
    },

    // Update organization
    updateOrganization: async (organizationId, organizationData) => {
        try {
            const response = await apiServices.put(`Organizations/${organizationId}`, organizationData);
            return response.data;
        } catch (error) {
            console.error("Error updating organization:", error);
            throw error;
        }
    },

    // Delete organization
    deleteOrganization: async (organizationId) => {
        try {
            const response = await apiServices.delete(`Organizations/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting organization:", error);
            throw error;
        }
    }
};

// ==================== AUTH API ====================
export const AuthAPI = {
    // Login
    login: async (email, password) => {
        try {
            const response = await apiServices.post('Auth/Login', {
                Email: email,
                Password: password
            });
            return response.data;
        } catch (error) {
            console.error("Login error:", error);
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.Message || 
                               error.message || 
                               "Login failed. Please check your credentials.";
            throw new Error(errorMessage);
        }
    },

    // Register
    register: async (userData) => {
        try {
            const response = await apiServices.post('Auth/register', userData);
            return response.data;
        } catch (error) {
            console.error("Registration error:", error);
            throw error;
        }
    }
};

// ==================== LEGACY API COMPATIBILITY ====================
// Keep existing API functions for backward compatibility
export const fetchOrders = async (organizationId = 1) => {
    try {
        const response = await PurchaseOrdersAPI.getAllPurchaseOrders(organizationId);
        return { data: response };
    } catch (error) {
        console.error("Error fetching orders:", error);
        throw error;
    }
};

export const GetItemsbyID = async (orgID, OrderID) => {
    try {
        const response = await PurchaseOrdersAPI.getPurchaseOrderById(OrderID);
        return response;
    } catch (error) {
        console.error("Error fetching order by ID:", error);
        throw error;
    }
};

export const GetItems = async (orgID) => {
    try {
        const response = await MedicationsAPI.getAllMedications(orgID);
        return response;
    } catch (error) {
        console.error("Error fetching items:", error);
        throw error;
    }
};

export const AddOrders = async (FormData, mainformData) => {
    try {
        const orderData = {
            supplierID: mainformData.supplierID || 0,
            orderDate: mainformData.orderDate || new Date().toISOString(),
            statusID: mainformData.statusID || 1, // Default to Draft status
            totalCost: parseFloat(mainformData.totalCost) || 0,
            comments: mainformData.comments || "",
            expectedDeliveryDate: mainformData.deliveryDate || new Date().toISOString(),
            shippingInstructions: mainformData.shippingInstructions || "",
            approvalDetails: mainformData.approvalDetails || "",
            cancellationReason: mainformData.cancellationReason || "",
            organizationID: mainformData.organizationID || 1,
            purchaseOrderDetails: FormData.map((item) => ({
                medicationID: item.itemID,
                orderedQuantity: item.quantity,
                receivedQuantity: item.receivedQuantity || 0,
                unitPrice: item.unitPrice,
                notes: item.comments || "",
            }))
        };

        const response = await PurchaseOrdersAPI.createPurchaseOrder(orderData);
        return response;
    } catch (error) {
        console.error("Error adding order:", error);
        throw error;
    }
};

export const GetStatuses = async () => {
    try {
        const response = await PurchaseOrdersAPI.getPurchaseOrderStatuses();
        return response;
    } catch (error) {
        console.error("Error fetching statuses:", error);
        throw error;
    }
};

export const GetSuuplier = async (OrgID) => {
    try {
        const response = await SuppliersAPI.getAllSuppliers(OrgID);
        return response;
    } catch (error) {
        console.error("Error fetching suppliers:", error);
        throw error;
    }
};

export const GetLogin = async (Data) => {
    try {
        const response = await AuthAPI.login(Data.Email, Data.Password);
        return response;
    } catch (error) {
        console.error("Login error:", error);
        throw error;
    }
};

export default {
    DashboardAPI,
    MedicationsAPI,
    PurchaseOrdersAPI,
    SuppliersAPI,
    CustomersAPI,
    AuthAPI,
    POSAPI,
    ProductSettingsAPI,
    UserManagementAPI,
    // Legacy compatibility
    fetchOrders,
    GetItemsbyID,
    GetItems,
    AddOrders,
    GetStatuses,
    GetSuuplier,
    GetLogin
};
